<template>
    <div class="position-relative">

        <!-- Loading overlay (completely hides main content) -->
        <div v-if="persisting || updatingDate" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">&nbsp;</span>
            </div>
            <p class="mt-3 mb-0">
                {{ persisting ? 'Confirming Faults, Please Wait.' : 'Confirming new date, please wait.' }}
            </p>
        </div>

        <!-- Main content only shows if NOT persisting/updating -->
        <div v-else>
            <!-- State 1: Already confirmed -->
            <template v-if="isConfirmed">
                <h5>Confirmed faults</h5>
                <p>You selected the below faults on {{ confirmedDate }}.</p>
                <ul class="list-group selection-list my-3">
                    <li
                        class="list-group-item mb-0"
                        v-for="problem in confirmedProblemTitles"
                        :key="problem.id"
                    >
                        {{ problem.title }}
                    </li>
                </ul>
            </template>

            <!-- State 2: Date not available -->
            <template v-else-if="showDatePicker">
                <div class="mt-3">
                    <p>{{ dateNotAvailableMessage }}</p>
                    <div class="date-options">
                        <button
                            v-for="date in availableDates"
                            :key="date"
                            class="btn btn-outline-primary m-1"
                            @click="updateRepairDate(date)"
                        >
                            {{ formatDate(date) }}
                        </button>
                    </div>
                </div>
            </template>

            <!-- State 3: Fault selection -->
            <template v-else>
                <h5>Select device faults</h5>
                <p class="text-sm mt-1 mb-4">
                    Before we can attend your repair we need to know what the faults are with your device.
                    Please select from the below options.
                </p>

                <h5>Frequently selected</h5>
                <ul class="list-group selection-list mt-3 mb-4">
                    <li
                        :class="{ 'pre-selected': bookedProblems.includes(problem.id) }"
                        class="list-group-item mb-0"
                        v-for="problem in highlightedProblems"
                        :key="problem.id"
                    >
                        <div class="custom-control custom-checkbox d-flex">
                            <input
                                type="checkbox"
                                :id="`problem-${problem.id}`"
                                :value="problem.id"
                                class="custom-control-input"
                                v-model="confirmedProblems"
                                :disabled="bookedProblems.includes(problem.id)"
                            />
                            <label
                                :for="`problem-${problem.id}`"
                                class="custom-control-label mb-0 flex-grow-1"
                            >
                                {{ problem.title }}
                            </label>
                        </div>
                    </li>
                </ul>

                <ul class="list-group selection-list my-3">
                    <li
                        :class="{ 'pre-selected': bookedProblems.includes(problem.id) }"
                        class="list-group-item mb-0"
                        v-for="problem in problems"
                        :key="problem.id"
                    >
                        <div class="custom-control custom-checkbox d-flex">
                            <input
                                type="checkbox"
                                :id="`problem-${problem.id}`"
                                :value="problem.id"
                                class="custom-control-input"
                                v-model="confirmedProblems"
                                :disabled="bookedProblems.includes(problem.id)"
                            />
                            <label
                                :for="`problem-${problem.id}`"
                                class="custom-control-label mb-0 flex-grow-1"
                            >
                                {{ problem.title }}
                            </label>
                        </div>
                    </li>
                </ul>

                <div v-if="cantConfirm" class="alert alert-danger">
                    You need to select one or more device fault to be able to confirm.
                </div>

                <button
                    class="btn btn-success w-100"
                    :disabled="cantConfirm"
                    @click="actionConfirm"
                >
                    Confirm Device Faults
                </button>
            </template>
        </div>
    </div>
</template>



<script>
import moment from "moment";

export default {
    props: {
        orderRef: {
            type: String,
            required: true,
        },
        device: {
            type: Object,
            required: true
        },
        useBookedProblems: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            bookedProblems: [],
            confirmedProblems: [],
            persisting: false,
            updatingDate: false,   // new flag
            confirmedAt: null,

            // New for DATE_NOT_AVAILABLE handling
            dateNotAvailableMessage: '',
            availableDates: [],
            showDatePicker: false
        }
    },
    computed: {
        highlightedProblems() {
            return this.device.problems
                .filter(problem => (problem.highlighted || problem.selected) && problem.b2c_applicable)
                .sort((a, b) => a.title.localeCompare(b.title));
        },
        problems() {
            return this.device.problems
                .filter(problem => !problem.highlighted && !problem.selected && problem.b2c_applicable)
                .sort((a, b) => a.title.localeCompare(b.title));
        },
        confirmedProblemTitles() {
            return this.device.problems.filter(problem => this.confirmedProblems.includes(problem.id))
        },
        isConfirmed() {
            return this.confirmedAt !== null;
        },
        confirmedDate() {
            return this.confirmedAt
                ? moment(this.confirmedAt).format('ddd Do MMM')
                : null;
        },
        cantConfirm() {
            return this.confirmedProblems.length === 0
        },
        persistingMessage() {
            return this.showDatePicker || this.updatingDate
                ? 'Confirming new date, please wait.'
                : 'Confirming Faults, Please Wait.';
        }
    },
    created() {
        const problems = this.device.problems
            .filter(problem => problem.selected)
            .map(problem => problem.id);

        this.confirmedAt = this.device.problems_confirmed_at;

        if (this.useBookedProblems || this.isConfirmed) {
            this.confirmedProblems = problems;
            this.bookedProblems = problems;
        }
    },
    methods: {
        actionConfirm() {
            if (this.cantConfirm) return;

            this.persisting = true;
            this.$http.patch(
                `api/v4/customer/${this.orderRef}/devices/${this.device.id}/confirm`,
                { problems: this.confirmedProblems }
            )
                .then((r) => {
                    this.confirmedAt = r.data.data.problems_confirmed_at;
                    this.persisting = false;
                })
                .catch((e) => {
                    const err = e.response?.data;
                    if (err?.code === 'OR04') {
                        this.dateNotAvailableMessage = err.messages?.message || 'The selected date is not available.';
                        this.availableDates = err.messages?.available_dates || [];
                        this.showDatePicker = true;
                    }
                    if (err?.code === 'OR09') {
                        this.showDatePicker = true;
                        this.dateNotAvailableMessage = err.messages?.message || 'Additional problems cannot be selected after the booking cut-off time. Please contact customer services.';
                    }
                    this.persisting = false;
                });
        },

        updateRepairDate(newDate) {
            this.updatingDate = true;
            this.showDatePicker = false;

            this.$http.patch(`api/v4/customer/${this.orderRef}/update-date`, {
                date: newDate,
                ref: this.orderRef
            })
                .then(() => {
                    this.updatingDate = false;
                    this.actionConfirm();
                })
                .catch((err) => {
                    console.error(err);
                    this.updatingDate = false;
                    this.showDatePicker = true; // show picker again if API fails
                });
        },

        getOrdinalSuffix(day) {
            if (day > 3 && day < 21) return 'th'; // for 11th, 12th, 13th
            switch (day % 10) {
                case 1:  return "st";
                case 2:  return "nd";
                case 3:  return "rd";
                default: return "th";
            }
        },

        formatDate(dateStr) {
            if (!dateStr) {
                return '';
            }

            const d = new Date(dateStr);

            // Get the full day of the week name
            const dayOfWeek = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(d);

            const day = d.getDate();
            const suffix = this.getOrdinalSuffix(day);
            const monthName = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(d);
            const year = d.getFullYear();

            // Add dayOfWeek to the beginning of the returned string
            return `${dayOfWeek} ${day}${suffix} ${monthName} ${year}`;
        }
    }
}
</script>

<style>
.loading-overlay {
    margin: 2rem auto; /* centers horizontally */
    padding: 2rem 3rem;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 300px;
    position: relative; /* stays in flow, not overlay */
}

.pre-selected {
    background: #fbe8cd;
}

.opacity-50 {
    opacity: 0.5;
}
.pointer-events-none {
    pointer-events: none;
}

</style>
